// src/components/QuickActions.js
import React, { useState, useRef, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Brain, 
  Download, 
  Upload, 
  Settings, 
  BarChart3,
  Sparkles,
  Zap,
  BookOpen,
  Users,
  FileText,
  FolderPlus,
  Share2,
  Star,
  Clock
} from 'lucide-react';
import './QuickActions.css';

const QuickActions = ({ onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredActions, setFilteredActions] = useState([]);
  const searchRef = useRef(null);

  const quickActions = [
    {
      id: 'create-flashcard',
      title: 'Create Flashcard',
      description: 'Create a new flashcard manually',
      icon: <Plus size={20} />,
      color: '#3b82f6',
      category: 'create',
      shortcut: 'N',
      action: () => onAction('create-flashcard')
    },
    {
      id: 'ai-generate',
      title: 'AI Generate',
      description: 'Generate flashcards using AI',
      icon: <Sparkles size={20} />,
      color: '#8b5cf6',
      category: 'ai',
      shortcut: 'G',
      action: () => onAction('ai-generate')
    },
    {
      id: 'quick-study',
      title: 'Quick Study',
      description: 'Start a study session',
      icon: <Brain size={20} />,
      color: '#10b981',
      category: 'study',
      shortcut: 'S',
      action: () => onAction('quick-study')
    },
    {
      id: 'import-export',
      title: 'Import/Export',
      description: 'Import or export your flashcards',
      icon: <Upload size={20} />,
      color: '#f59e0b',
      category: 'manage',
      shortcut: 'I',
      action: () => onAction('import-export')
    },
    {
      id: 'create-deck',
      title: 'Create Deck',
      description: 'Create a new flashcard deck',
      icon: <FolderPlus size={20} />,
      color: '#ec4899',
      category: 'create',
      shortcut: 'D',
      action: () => onAction('create-deck')
    },
    {
      id: 'search-cards',
      title: 'Search Cards',
      description: 'Search through your flashcards',
      icon: <Search size={20} />,
      color: '#6366f1',
      category: 'navigate',
      shortcut: '/',
      action: () => onAction('search-cards')
    },
    {
      id: 'stats',
      title: 'View Stats',
      description: 'View your learning statistics',
      icon: <BarChart3 size={20} />,
      color: '#06b6d4',
      category: 'analyze',
      shortcut: 'T',
      action: () => onAction('stats')
    },
    {
      id: 'settings',
      title: 'Settings',
      description: 'Open settings',
      icon: <Settings size={20} />,
      color: '#6b7280',
      category: 'manage',
      shortcut: ',',
      action: () => onAction('settings')
    },
    {
      id: 'share-deck',
      title: 'Share Deck',
      description: 'Share a deck with others',
      icon: <Share2 size={20} />,
      color: '#0ea5e9',
      category: 'share',
      shortcut: 'P',
      action: () => onAction('share-deck')
    },
    {
      id: 'quick-review',
      title: 'Quick Review',
      description: 'Review due flashcards',
      icon: <Clock size={20} />,
      color: '#f97316',
      category: 'study',
      shortcut: 'R',
      action: () => onAction('quick-review')
    },
    {
      id: 'ai-analyze',
      title: 'AI Analyze',
      description: 'Get AI insights on your learning',
      icon: <Zap size={20} />,
      color: '#d946ef',
      category: 'ai',
      shortcut: 'A',
      action: () => onAction('ai-analyze')
    },
    {
      id: 'community',
      title: 'Community',
      description: 'Browse community decks',
      icon: <Users size={20} />,
      color: '#84cc16',
      category: 'discover',
      shortcut: 'C',
      action: () => onAction('community')
    }
  ];

  const categories = {
    create: 'Create',
    ai: 'AI Tools',
    study: 'Study',
    manage: 'Manage',
    navigate: 'Navigate',
    analyze: 'Analytics',
    share: 'Share',
    discover: 'Discover'
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + K to open quick actions
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => searchRef.current?.focus(), 100);
      }
      
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
      
      // Check for action shortcuts when quick actions is open
      if (isOpen) {
        const action = quickActions.find(a => 
          a.shortcut.toLowerCase() === e.key.toLowerCase()
        );
        if (action) {
          e.preventDefault();
          action.action();
          setIsOpen(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Filter actions based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredActions(quickActions);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = quickActions.filter(action => 
      action.title.toLowerCase().includes(query) ||
      action.description.toLowerCase().includes(query) ||
      action.category.toLowerCase().includes(query)
    );
    setFilteredActions(filtered);
  }, [searchQuery]);

  const handleActionClick = (action) => {
    action.action();
    setIsOpen(false);
    setSearchQuery('');
  };

  if (!isOpen) {
    return (
      <button 
        className="quick-actions-trigger"
        onClick={() => setIsOpen(true)}
        title="Quick Actions (Ctrl+K)"
      >
        <Zap size={18} />
        <span>Quick Actions</span>
        <kbd>⌘K</kbd>
      </button>
    );
  }

  return (
    <div className="quick-actions-overlay" onClick={() => setIsOpen(false)}>
      <div className="quick-actions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quick-actions-header">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search actions or type a command..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
              >
                ×
              </button>
            )}
          </div>
          <button 
            className="close-button"
            onClick={() => setIsOpen(false)}
          >
            Esc
          </button>
        </div>

        <div className="quick-actions-body">
          {searchQuery ? (
            <div className="search-results">
              <div className="results-count">
                {filteredActions.length} result{filteredActions.length !== 1 ? 's' : ''}
              </div>
              {filteredActions.map((action) => (
                <QuickActionItem 
                  key={action.id} 
                  action={action} 
                  onClick={() => handleActionClick(action)}
                />
              ))}
            </div>
          ) : (
            <div className="categories-grid">
              {Object.entries(categories).map(([categoryId, categoryName]) => {
                const categoryActions = quickActions.filter(
                  action => action.category === categoryId
                );
                
                if (categoryActions.length === 0) return null;
                
                return (
                  <div key={categoryId} className="category-section">
                    <h3 className="category-title">{categoryName}</h3>
                    <div className="category-actions">
                      {categoryActions.map((action) => (
                        <QuickActionItem 
                          key={action.id} 
                          action={action} 
                          onClick={() => handleActionClick(action)}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="quick-actions-footer">
          <div className="footer-hint">
            <span className="hint-item">
              <kbd>↑</kbd><kbd>↓</kbd> Navigate
            </span>
            <span className="hint-item">
              <kbd>↩</kbd> Select
            </span>
            <span className="hint-item">
              <kbd>esc</kbd> Close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuickActionItem = ({ action, onClick, compact = false }) => {
  return (
    <button 
      className={`quick-action-item ${compact ? 'compact' : ''}`}
      onClick={onClick}
    >
      <div className="action-icon" style={{ backgroundColor: `${action.color}20` }}>
        <span style={{ color: action.color }}>
          {action.icon}
        </span>
      </div>
      <div className="action-content">
        <div className="action-title">
          {action.title}
          {action.shortcut && (
            <kbd className="action-shortcut">{action.shortcut}</kbd>
          )}
        </div>
        {!compact && (
          <div className="action-description">{action.description}</div>
        )}
      </div>
    </button>
  );
};

export default QuickActions;